import { Add, Delete, Edit } from "@mui/icons-material";
import { IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import useDebounce from "../../hooks/useDebounce"; 
import {
  getAssociatedUsers,
  removeAssociatedUsers,
} from "../../modules/project";
import { projectTeamHeader } from "../../static-data/users";
import ConfirmationModal from "../elements/ConfirmationModal";
import Loader from "../elements/Loader";
import TableComponent from "../elements/TableComponent";
import TeamTableAction from "./TeamTableAction";
import CustomIconButton from "../elements/CustomIconButton";
import SearchBar from "../elements/SearchBar"; 

const ProjectUsersList = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState("desc");
  const { projectId } = useParams();
  const [userToRemove, setUserToRemove] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [mode, setMode] = useState("add");
  const [searchValue, setSearchValue] = useState(""); 
  const debouncedSearchQuery = useDebounce(searchValue, 500); 

  const currentUserId = JSON.parse(localStorage.getItem("user")).id;

  const handleDeleteClick = (user) => {
    setOpenDelete(true);
    setUserToRemove(user);
  };

  const handleEditClick = (user) => {
    setOpenAdd(true);
    setUserToRemove(user);
    setMode("edit");
  };

  const handleDeleteCancel = () => {
    setOpenDelete(false);
    setUserToRemove(null);
    setError(null);
  };

  const fetchUsersData = async () => {
    setLoading(true);
    setError(null);
    try {
      const usersList = await getAssociatedUsers(projectId, searchValue);
      setUsers(usersList);
      // Find current user and check their role
      const currentUser = usersList.find(
        (item) => item.user_id === currentUserId
      );
      if (currentUser) {
        setIsAdmin(
          currentUser.role === "admin" || currentUser.role === "org-admin" || currentUser.role === 'org-manager'
        );
      }
    } catch (error) {
      setError("Failed to fetch users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersData();
  }, [projectId, debouncedSearchQuery]);

  const handleDelete = async () => {
    try {
      await removeAssociatedUsers(userToRemove?.id);
      fetchUsersData();
      handleDeleteCancel();
    } catch (error) {
      setError(error.message || "Failed to update project. Please try again.");
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  return (
    <Container fluid>
      <Row className="align-items-center">
        <Col>
          <Typography variant="h4">Project Team</Typography>
        </Col>
        {isAdmin && (
          <Col className="d-flex justify-content-end">
            <Button variant="primary" onClick={() => setOpenAdd(true)}>
              Manage Team
            </Button>
          </Col>
        )}
      </Row>
      <Row className="align-items-center mt-3">
        <Col md="4">
          <SearchBar
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            clearSearch={() => handleSearch('')}
            placeholder="Search by name or email"
          />
        </Col>
      </Row>
      <Row>
        <Col>
          {loading ? (
            <Loader />
          ) : (
            <TableComponent
              data={
                users &&
                (users?.map((item) => ({
                  ...item,
                  name: item?.user?.full_name,
                  email: item?.user?.email,
                  status: item?.user?.status,
                })) ||
                  [])
              }
              headerTabs={projectTeamHeader}
              order={order}
              setOrder={setOrder}
              defaultSort="created_at"
              isLoading={loading}
              striped
              action={(row) =>
                isAdmin ? (
                  row.role !== "org-admin" && row.role !== 'org-manager' && row.user_id !== currentUserId ? (
                    <>
                      <CustomIconButton
                        title="Edit role"
                        disabled={row.user_id === currentUserId}
                        onClick={() => handleEditClick(row)}
                      >
                        <Edit sx={{ color: "var(--secondary-color)" }} />
                      </CustomIconButton>

                      <CustomIconButton
                        title="Remove from project"
                        onClick={() => handleDeleteClick(row)}
                      >
                        <Delete sx={{ color: "red" }} />
                      </CustomIconButton>
                    </>
                  ) : null
                ) : null
              }
            />
          )}
        </Col>
      </Row>
      <ConfirmationModal
        show={openDelete}
        handleConfirm={handleDelete}
        handleClose={handleDeleteCancel}
        title="Remove User from Project"
        body="Are you sure you want to remove this user from the project?"
      />
      <TeamTableAction
        fetchUsersData={fetchUsersData}
        users={users}
        setOpenModal={setOpenAdd}
        open={openAdd}
        mode={mode}
        setMode={setMode}
        selectedUser={userToRemove}
      />
    </Container>
  );
};

export default ProjectUsersList;
